import * as React from "react"
import { StandardPhraseSelection } from "../../../../StandardPhraseSelection/StandardPhraseSelection";
import { IStandardPhrase } from "../../../../../models/IStandardPhrase";
import { IOrthodonticReferralTreatmentDecision, IOrthodonticReferralDecision, IOrthodonticReferralConditionCode, IOrthodonticReferralTreatmentNotNeededReason } from "../../../../../models/IOrthodonticReferral";
import { HiddenInput, Select } from "soprasteria-ui-library";
import { IListItem } from "soprasteria-ui-library/dist/models/IFormInput";

interface IOrthodonticReferralTreatmentDecisionProps {
    orthodonticReferralId: number;
    standardPhrases: IStandardPhrase[] | undefined;
    decisions: IOrthodonticReferralDecision[] | undefined;
    conditionCodes: IOrthodonticReferralConditionCode[] | undefined;
    treatmentNotNeededReasons: IOrthodonticReferralTreatmentNotNeededReason[] | undefined;
    getStandardPhrases: () => void;
    onFormSave: (data: IOrthodonticReferralTreatmentDecision) => void;
    onFormSubmit: (data: IOrthodonticReferralTreatmentDecision) => void;
    isSendingDecision: boolean;
    showStandardPhraseTable: boolean;
    setShowStandardPhraseTable: React.Dispatch<React.SetStateAction<boolean>>;
    changeSuccessMessage: (message: string) => void;
    changeErrorMessage: (message: string) => void;
    awaitingDecisionMotivationText?: string;
}

export const OrthodonticReferralTreatmentDecision: React.FunctionComponent<IOrthodonticReferralTreatmentDecisionProps> = ({ orthodonticReferralId, standardPhrases, decisions, conditionCodes, treatmentNotNeededReasons, getStandardPhrases, onFormSave, onFormSubmit, isSendingDecision, showStandardPhraseTable, setShowStandardPhraseTable, changeSuccessMessage, changeErrorMessage, awaitingDecisionMotivationText }) => {
    const [selectedDecisionId, setSelectedDecisionId] = React.useState<number>();

    const isTreatmentNotNeededReasonNeeded = (): boolean => {
        if (decisions) {
            const isTreatmentNotNeededReasonNeeded = decisions.some((decision) => decision.requireTreatmentNotNeededReasonId && decision.orthodonticReferralDecisionId === selectedDecisionId && decision.canSelectTreatmentNotNeededReason);
            return isTreatmentNotNeededReasonNeeded;
        }
        return false;
    }

    const isConditionCodeNeeded = (): boolean => {
        if (decisions) {
            const isConditionCodeNeeded = decisions.some((decision) => decision.requireConditionCodeId && decision.orthodonticReferralDecisionId === selectedDecisionId);
            return isConditionCodeNeeded;
        }
        return false;
    }

    return (
        <>
            <StandardPhraseSelection
                standardPhrases={standardPhrases}
                changeSuccessMessage={changeSuccessMessage}
                changeErrorMessage={changeErrorMessage}
                standardPhraseFeature="orthodonticReferralDecision"
                getStandardPhrases={getStandardPhrases}
                saveForm={onFormSave}
                submitForm={onFormSubmit}
                isSubmittingForm={isSendingDecision}
                showStandardPhraseTable={showStandardPhraseTable}
                setShowStandardPhraseTable={setShowStandardPhraseTable}
                defaultValue={awaitingDecisionMotivationText}
                isRequired={true}
                additionalFormInputs={
                    <>
                        <HiddenInput name="orthodonticReferralId" value={orthodonticReferralId.toString()} />
                        <Select label="Beslut" name="orthodonticReferralDecisionId"
                            options={decisions?.map<IListItem>((decision) => { return { value: decision.orthodonticReferralDecisionId.toString(), text: decision.description } }) ?? []}
                            isLoading={!decisions} placeholder="Välj ett beslut" required inlineLabel isClearable onChange={(val) => setSelectedDecisionId(parseInt(val))} />
                        {
                            isConditionCodeNeeded() ?
                                <Select label="Tillståndskod" name="conditionCodeId"
                                    options={conditionCodes?.map<IListItem>((conditionCode) => { return { value: conditionCode.orthodonticReferralConditionCodeId.toString(), text: `${conditionCode.conditionCode} - ${conditionCode.diagnosis}` } }) ?? []}
                                    isLoading={!conditionCodes} placeholder="Välj en tillståndskod" required inlineLabel isClearable />
                                : null
                        }
                        {
                            isTreatmentNotNeededReasonNeeded() ?
                                <Select label="Subkategori" name="orthodonticReferralTreatmentNotNeededReasonId"
                                    options={treatmentNotNeededReasons?.map<IListItem>((reason) => { return { value: reason.orthodonticReferralTreatmentNotNeededReasonId.toString(), text: reason.description } }) ?? []}
                                    isLoading={!treatmentNotNeededReasons} placeholder="Välj en subkategori" required inlineLabel isClearable />
                                : null
                        }
                    </>
                }
            >
            </StandardPhraseSelection>
        </>
    );
}